import React from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Typography from '@mui/material/Typography';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';

interface StatCardProps {
  label: string;
  value: number;
  change: number;
  color: string;
  icon: React.ReactNode;
  format?: 'number' | 'currency' | 'percent';
}

function formatValue(value: number, format: 'number' | 'currency' | 'percent'): string {
  switch (format) {
    case 'currency':
      return '$' + value.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    case 'percent':
      return value.toFixed(2) + '%';
    case 'number':
    default:
      return value.toLocaleString();
  }
}

const StatCard: React.FC<StatCardProps> = ({ label, value, change, color, icon, format = 'number' }) => {
  const isPositive = change > 0;
  const isNegative = change < 0;

  const changeColor = isPositive ? '#00E5A0' : isNegative ? '#FF6B6B' : undefined;

  return (
    <Card
      sx={{
        p: '20px',
        borderLeft: `4px solid ${color}`,
        transition: 'box-shadow 0.2s ease-in-out',
        '&:hover': {
          boxShadow: 8,
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: `${color}1F`,
            color: color,
            flexShrink: 0,
          }}
        >
          {icon}
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
            {label}
          </Typography>

          <Typography variant="h4" sx={{ fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>
            {formatValue(value, format)}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            {isPositive && <TrendingUpIcon sx={{ fontSize: 16, color: changeColor }} />}
            {isNegative && <TrendingDownIcon sx={{ fontSize: 16, color: changeColor }} />}
            <Typography
              variant="caption"
              sx={{ color: changeColor ?? 'text.secondary', fontWeight: 600 }}
            >
              {isPositive && '+'}
              {change.toFixed(1)}%
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', ml: 0.5 }}>
              vs prev period
            </Typography>
          </Box>
        </Box>
      </Box>
    </Card>
  );
};

export default StatCard;
